import { DocumentTypeCat } from '@ps/platform-message-framework/common/DocumentTypeCat';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Currency, ErrorCode } from '@ps/web-core-enums';
import type { AccountPayload } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import type { StarsAutocompleteOption } from '@ps/web-shared-components';
import { format } from 'date-fns';
import { isEmpty, isUndefined } from 'lodash';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import { PEPHIOOptions } from '@swf/account';
import { JumpDirection } from '@swf/components/StarsWizard/components/Navigation/Navigation';
import { CRMZone, Country, LimitType } from '@swf/core/enums';
import useRequest from '@swf/core/hooks/useRequest';
import { type Dispatch, dispatch, select } from '@swf/core/store';
import type { WizardContext } from '@swf/core/types/Wizard';
import { formatPhoneNumber } from '@swf/core/utils/phoneVerification';
import occupations from '@swf/generated/occupations.json';

import useCRMBonusCode from '../../../hooks/useCRMBonusCode';
import useSetMitIdData from '../../../hooks/useSetMitIdData';
import { CashierFlowType, ProgressBarSteps } from '../../../types/Cashier';
import type { Fields } from '../../../types/Cashier';
import type {
  CrossOperatorDepositLimitPayload,
  ResponsibleGamingLimitsPayload
} from '../../../types/responsibleGamingLimits';
import { PaySystemErrorCode, VerificationErrorCode } from '../../../types/status';
import { updateFormFieldsValues } from '../../../utils/cashier';
import { composePhoneNumber } from '../../../utils/composePhoneNumber';
import { deRmfHandleResponse } from '../../../utils/deRmfHandleResponse';
import { handleEffectResponse } from '../../../utils/handleEffectResponse';
import type { PersonalIdType } from '../../../utils/personalIdType';
import { getPersonalIdType } from '../../../utils/personalIdType';
import {
  getDepositLimitsPayload,
  getLossLimitsPayload,
  getPlayTimeLimitPayload
} from '../../../utils/responsibleGamingLimits';
import { aliasFieldName } from '../components/RMFLegalName/components/Alias/Alias';
import { pokerSliderFieldName } from '../components/RMFPokerTimeLimit/components/PokerSlider/PokerSlider';
import { getGenderDK, isDenmarkCitizen } from '../utils/rmf';

import useRmfGuards from './useRmfGuards';

export const MINS_PER_HOUR = 60;

export enum Service {
  RmfAccountFetch = 'rmfAccountFetch',
  RmfAccumulatedLimitSave = 'rmfAccumulatedLimitSave',
  RmfAddressDESave = 'rmfAddressDESave',
  RmfAddressSave = 'rmfAddressSave',
  RmfAliasSave = 'rmfAliasSave',
  RmfAutomaticWithdrawalLimitSave = 'rmfAutomaticWithdrawalLimitSave',
  RmfBirthNameSave = 'rmfBirthNameSave',
  RmfCitizenshipSave = 'rmfCitizenshipSave',
  RmfCrossOperatorDepositLimitSave = 'rmfCrossOperatorDepositLimitSave',
  RmfDepositLimitSave = 'rmfDepositLimitSave',
  RmfFetchApprovedLossLimits = 'rmfFetchApprovedLossLimits',
  RmfFetchUserDepositLimit = 'rmfFetchUserDepositLimit',
  RmfDobUpdate = 'rmfDobUpdate',
  RmfDocumentsUpdate = 'rmfDocumentsUpdate',
  RmfEnableRealMoneyFlag = 'rmfEnableRealMoneyFlag',
  RmfGenderSave = 'rmfGenderSave',
  RmfGetBankDetailsInfo = 'rmfGetBankDetailsInfo',
  RmfGetFiscalCodeInfo = 'rmfGetFiscalCodeInfo',
  RmfIdentifyVerificationSave = 'rmfIdentifyVerificationSave',
  RmfIdentifyVerificationSavePT = 'rmfIdentifyVerificationSavePT',
  RmfIdentifyVerificationSaveDK = 'rmfIdentifyVerificationSaveDK',
  RmfIdentifyVerificationSaveGR = 'RmfIdentifyVerificationSaveGR',
  RmfLegalNameSave = 'rmfLegalNameSave',
  RmfLossLimitSave = 'rmfLossLimitSave',
  RmfGRPlayingTimeSave = 'rmfGRPlayingTimeSave',
  RmfMiddleNameSave = 'rmfMiddleNameSave',
  RmfOccupationPEPSave = 'rmfOccupationPEPSave',
  RmfOccupationSave = 'rmfOccupationSave',
  RmfPlaceOfBirthSave = 'rmfPlaceOfBirthSave',
  RmfPokerTimeSave = 'rmfPokerTimeSave',
  RmfMobilePhoneSave = 'rmfMobilePhoneSave',
  RmfMobilePhoneVerificationSave = 'rmfMobilePhoneVerificationSave',
  RmfRealMoneyFlagAndAddressUpdate = 'rmfRealMoneyFlagAndAddressUpdate',
  RmfRealMoneyInformationCheck = 'rmfRealMoneyInformationCheck',
  RmfUpdateFormData = 'rmfUpdateFormData',
  RmfUpdateUSPAUserData = 'rmfUpdateUSPAUserData',
  RmfUpdateCanadaFormFields = 'rmfUpdateCanadaFormFields',
  RmfSecurityQuestionsSave = 'rmfSecurityQuestionsSave',
  RmfSSNSave = 'rmfSSNSave',
  RmfSSNConfirm = 'rmfSSNConfirm',
  RmfUSPASSNConfirm = 'rmfUSPASSNConfirm',
  RmfTermsOfServiceSave = 'rmfTermsOfServiceSave',
  RmfUpdateBankDetailsInfo = 'rmfUpdateBankDetailsInfo',
  SetCRMBonus = 'setCRMBonus',
  SetJumpCreator = 'setJumpCreator',
  SignupDobFetch = 'signupDobFetch',
  RmfUserDataSave = 'rmfUserDataSave',
  RmfUSPAUserDataSave = 'rmfUSPAUserDataSave',
  RmfRetryCAONUserDataSave = 'rmfRetryCAONUserDataSave',
  RmfRetryUserDataSave = 'rmfRetryUserDataSave',
  RmfRetryFRUserDataSave = 'rmfRetryFRUserDataSave',
  RmfRetryDEUserDataSave = 'rmfRetryDEUserDataSave',
  RmfRetryInvokeRealMoneyOk = 'rmfRetryInvokeRealMoneyOk',
  RmfTermsOfServiceWithoutLimitSave = 'rmfTermsOfServiceWithoutLimitSave',
  RmfEnableRealMoneyFlagDE = 'rmfEnableRealMoneyFlagDE',
  RmfEnableRealMoneyFlagItaly = 'rmfEnableRealMoneyFlagItaly',
  RmfEnableRealMoneyFlagSpain = 'rmfEnableRealMoneyFlagSpain',
  RmfEnableRealMoneyFlagDK = 'rmfEnableRealMoneyFlagDK',
  RmfMitIdData = 'rmfMitIdData'
}

const useRmfServicesData = () => {
  const request = useRequest();
  const setCRMBonus = useCRMBonusCode(CRMZone.SWC_WELCOME_BONUS);
  const guards = useRmfGuards();
  const siteId = useSelector(select.cms.getSiteId);
  const { citizenship, occupationInfo } = useSelector(select.account.get);
  const isItaly = siteId === Sites.ITA;
  const isDe = useSelector(select.cms.getSiteId) === Sites.DE;
  const setMitIdData = useSetMitIdData();
  const {
    account: { mobileVerificationCode, updateAccount, fetch: accountFetch, updatePEPHIOSelection }
  } = useDispatch<Dispatch>();

  const signupDobFetch = useCallback(async () => {
    const {
      body: { dateOfBirth }
    } = await lastValueFrom(request<Record<string, string>>({ service: 'wc3.user.get.userInfo' }));
    return { userInfo: { dateOfBirth } };
  }, [request]);

  const rmfDobUpdate = useCallback(
    ({ values }: { values: { dob: Date } }) =>
      updateAccount({
        dob: format(values.dob, 'yyyy-LL-dd'),
        fetchAccount: isItaly
      }),
    [updateAccount, isItaly]
  );

  const rmfLegalNameSave = useCallback(
    async ({ values: { firstName, lastName } }: Pick<WizardContext, 'values'>) => {
      const response = await updateAccount({ firstName, lastName });
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfMiddleNameSave = useCallback(
    async ({ values: { middleName } }: { values: { middleName: string } }) => {
      if (isUndefined(middleName) || isEmpty(middleName)) return Promise.resolve({});

      const response = await updateAccount({ middleName });
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfBirthNameSave = useCallback(
    async ({ values: { birthName } }: { values: { birthName: string } }) => {
      const response = await updateAccount({ birthName: birthName ? birthName : '-' });
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfUserDataSave = useCallback(
    async ({
      values: {
        firstName,
        lastName,
        dialingCode,
        mobilePhoneNumber,
        streetAddress,
        city,
        state,
        zip,
        country,
        playerFundsProtection
      }
    }: Pick<WizardContext, 'values'>) => {
      const mobilePhone = composePhoneNumber(dialingCode, mobilePhoneNumber);
      const response = await updateAccount({
        firstName,
        lastName,
        mobilePhone,
        streetAddress,
        city,
        state,
        zip,
        country,
        pfpChecked: playerFundsProtection
      });

      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfUSPAUserDataSave = useCallback(
    async ({
      values: { firstName, lastName, gender, streetAddress, city, state, zip, country }
    }: Pick<WizardContext, 'values'>) => {
      const response = await updateAccount({
        firstName,
        lastName,
        gender,
        streetAddress,
        city,
        state,
        zip,
        country
      });

      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfAddressDESave = useCallback(
    async ({
      values: { city, state, zip, country, streetName, buildingNumber }
    }: Pick<WizardContext, 'values'>) => {
      const payload: Partial<AccountPayload> = {
        streetAddress: `${streetName} ${buildingNumber}`,
        city,
        state,
        zip,
        country,
        streetName,
        buildingNumber
      };
      const response = await updateAccount(payload);
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfAddressSave = useCallback(
    async ({
      values: { streetAddress, city, state, zip, country }
    }: Pick<WizardContext, 'values'>) => {
      const payload: Partial<AccountPayload> = {
        streetAddress,
        city,
        state,
        zip,
        country
      };
      if (siteId === Sites.SPA && country === Country.Spain) {
        payload.taxRegion = state;
        delete payload.state;
      }
      const response = await updateAccount(payload);
      return handleEffectResponse(response);
    },
    [siteId, updateAccount]
  );

  const rmfRealMoneyFlagAndAddressUpdate = useCallback(
    async ({
      values: { streetAddress, city, state, zip, country, streetName, buildingNumber }
    }: Pick<WizardContext, 'values'>) => {
      const payload: Partial<AccountPayload & { taxRegion?: string; state?: string }> = {
        realMoneyFlag: true,
        streetAddress: isDe ? `${streetName} ${buildingNumber}` : streetAddress,
        city,
        zip,
        country
      };

      payload.state = state;

      const response = await updateAccount(payload);

      if (!response.success) {
        return handleEffectResponse(response);
      }

      dispatch.feature.check();

      return { rmfPassed: true, progressBarStep: ProgressBarSteps.AboutYou };
    },
    [isDe, updateAccount]
  );

  const rmfRetryFRUserDataSave = useCallback(
    async ({
      values: {
        firstName,
        lastName,
        streetAddress,
        city,
        state,
        zip,
        country,
        countryOfBirth,
        stateOfBirth,
        cityOfBirth
      }
    }: Pick<WizardContext, 'values'>) => {
      const response = await updateAccount({
        firstName,
        lastName,
        streetAddress,
        city,
        state,
        zip,
        country,
        placeOfBirthInfo: { countryOfBirth, stateOfBirth, cityOfBirth }
      });

      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfRetryDEUserDataSave = useCallback(
    async ({
      values: {
        firstName,
        lastName,
        birthName,
        streetName,
        buildingNumber,
        city,
        state,
        zip,
        country,
        countryOfBirth,
        stateOfBirth,
        cityOfBirth
      }
    }: Pick<WizardContext, 'values'>) => {
      const response = await updateAccount({
        firstName,
        lastName,
        birthName,
        streetName,
        buildingNumber,
        city,
        state,
        zip,
        country,
        placeOfBirthInfo: { countryOfBirth, stateOfBirth, cityOfBirth }
      });

      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfRetryCAONUserDataSave = useCallback(
    async ({
      values: { firstName, lastName, middleName, streetName, city, state, zip, country }
    }: Pick<WizardContext, 'values'>) => {
      const response = await updateAccount({
        firstName,
        lastName,
        middleName,
        streetName,
        city,
        state,
        zip,
        country
      });
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfGetFiscalCodeInfo = useCallback(
    async ({
      values,
      data: { userInfoFields }
    }: {
      values: Record<string, string>;
      data: { userInfoFields: { name: string; editable: boolean }[] };
    }) => {
      const { nationalDocumentNumber, cityOfBirth, countryOfBirth, stateOfBirth } = values;

      const field = userInfoFields?.find(
        ({ name }: { name: string }) => name === 'nationalDocumentNumber'
      );
      const isDocNumDisabled = !!field && !field.editable;

      const response = await updateAccount({ nationalDocumentNumber });
      await handleEffectResponse(response);

      if (!isDocNumDisabled && (cityOfBirth || countryOfBirth || stateOfBirth)) {
        delete values.cityOfBirth;
        delete values.countryOfBirth;
        delete values.stateOfBirth;
      }

      const {
        body: { provinceCities, countries }
      } = await lastValueFrom(
        request({
          service: 'wc3.user.get.italyBirthPlace',
          body: { fiscalCode: nationalDocumentNumber }
        })
      );

      return { italyBirthPlace: { countries, provinceCities } };
    },
    [request, updateAccount]
  );

  const rmfIdentifyVerificationSave = useCallback(
    async ({
      values: {
        citizenship,
        state,
        nationalDocumentNumber,
        docNumber,
        docType,
        country,
        docOtherTypeName,
        secondLastName
      }
    }: Pick<WizardContext, 'values'>) => {
      const payload: {
        secondLastName?: string;
        personalIdType?: PersonalIdType;
        citizenship: string;
        nationalDocumentNumber: string;
        docNumber: string;
        docOtherTypeName?: string;
        taxRegion?: string;
      } = {
        personalIdType: getPersonalIdType(docType),
        citizenship,
        nationalDocumentNumber,
        docNumber,
        docOtherTypeName,
        secondLastName
      };

      if (citizenship === Country.Spain || country === Country.Spain) {
        delete payload.secondLastName;
      }

      if (country === Country.Spain) {
        payload.taxRegion = state;
      }

      if (
        citizenship !== Country.Spain &&
        country !== Country.Spain &&
        docType !== DocumentTypeCat.OTHER
      ) {
        delete payload.docOtherTypeName;
      }

      const response = await updateAccount(payload);
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfIdentifyVerificationSaveDK = useCallback(
    async ({
      values: {
        citizenship,
        nationalDocumentNumber,
        docNumber,
        docType,
        docIssueDate,
        docIssuePlace,
        gender,
        intentToPlay
      }
    }: Pick<WizardContext, 'values'>) => {
      const payload: {
        citizenship: string;
        nationalDocumentNumber?: string;
        docNumber?: string;
        docType?: number;
        docIssueDate?: string;
        docIssuePlace?: string;
        gender?: string;
        intentToPlay?: number;
      } = {
        citizenship,
        nationalDocumentNumber
      };

      if (!isDenmarkCitizen(citizenship)) {
        payload.docNumber = docNumber;
        payload.docType = docType;
        payload.docIssueDate = docIssueDate;
        payload.docIssuePlace = docIssuePlace;
        payload.gender = gender;
        delete payload.nationalDocumentNumber;
      } else {
        payload.intentToPlay = intentToPlay;
        payload.nationalDocumentNumber = nationalDocumentNumber;
        payload.gender = getGenderDK(nationalDocumentNumber);
      }

      const response = await updateAccount(payload);
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfIdentifyVerificationSavePT = useCallback(
    async ({
      values: { citizenship, nationalDocumentNumber, docNumber, docType, profession }
    }: Pick<WizardContext, 'values'>) => {
      const payload: {
        citizenship: string;
        nationalDocumentNumber: string;
        docNumber: string;
        docType?: number;
        profession?: string;
      } = {
        citizenship,
        nationalDocumentNumber,
        docNumber,
        docType,
        profession
      };
      if (citizenship === Country.Portugal) {
        payload.docType = 1;
      }

      const response = await updateAccount(payload);
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfIdentifyVerificationSaveGR = useCallback(
    async ({
      values: { citizenship, nationalDocumentNumber, docType }
    }: Pick<WizardContext, 'values'>) => {
      const payload: {
        citizenship: string;
        nationalDocumentNumber: string;
        docType: number;
      } = {
        citizenship,
        nationalDocumentNumber,
        docType
      };

      const response = await updateAccount(payload);

      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfDepositLimitSave = useCallback(
    ({
      values: {
        rmf: { deposit }
      }
    }: {
      values: { rmf: { deposit: ResponsibleGamingLimitsPayload & { skip?: boolean } } };
    }) => {
      if (deposit.skip) return Promise.resolve({ ...deposit });
      const payload = getDepositLimitsPayload(deposit);

      return lastValueFrom(
        request({
          service: 'responsiblegaming.updateuserlimit',
          body: payload
        }),
        { defaultValue: undefined }
      );
    },
    [request]
  );

  const rmfCrossOperatorDepositLimitSave = useCallback(
    ({
      values: {
        rmf: { crossOperatorLimit }
      }
    }: {
      values: {
        rmf: {
          crossOperatorLimit: CrossOperatorDepositLimitPayload & { skip?: boolean };
        };
      };
    }) => {
      if (crossOperatorLimit.skip) return Promise.resolve({ ...crossOperatorLimit });

      return lastValueFrom(
        request({
          service: 'responsiblegaming.updateLugasLimit',
          body: { limitAmount: Number(crossOperatorLimit.limit) * 100 }
        }),
        { defaultValue: undefined }
      );
    },
    [request]
  );

  const rmfFetchUserDepositLimit = useCallback(async () => {
    const { body } = await lastValueFrom(request({ service: 'responsiblegaming.userlimit' }));

    return { depositLimits: body };
  }, [request]);

  const rmfFetchApprovedLossLimits = useCallback(async () => {
    const { body } = await lastValueFrom(
      request({ service: 'responsiblegaming.allapprovedlimits' })
    );

    return { lossLimits: body?.limits ?? [] };
  }, [request]);

  const rmfRealMoneyInformationCheck = useCallback(async () => {
    const {
      body: { realMoneyInformation }
    } = await lastValueFrom(request({ service: 'wc3.cashout.get.status' }));

    return { realMoneyInformation };
  }, [request]);

  const rmfUpdateFormData = useCallback(
    async ({ data, values }: WizardContext) => {
      if (!data.userInfoFields) {
        const { realMoneyInformation } = await rmfRealMoneyInformationCheck();
        data.userInfoFields = realMoneyInformation?.userInfoFields;
      }
      updateFormFieldsValues(values, data.userInfoFields);
    },
    [rmfRealMoneyInformationCheck]
  );

  const rmfUpdateUSPAUserData = useCallback(
    async (context: WizardContext) => {
      const { realMoneyInformation } = await rmfRealMoneyInformationCheck();
      context.data.userInfoFields = realMoneyInformation?.userInfoFields;
      updateFormFieldsValues(context.values, context.data.userInfoFields);
    },
    [rmfRealMoneyInformationCheck]
  );

  const rmfUpdateCanadaFormFields = useCallback(
    async ({ values }: WizardContext) => {
      const additionalInfo = Object.entries({ citizenship, ...occupationInfo }).map(
        ([name, value]) => ({
          name,
          value: value as string | number | null | boolean
        })
      );
      if (!additionalInfo) return;
      updateFormFieldsValues(values, additionalInfo);
    },
    [citizenship, occupationInfo]
  );

  const rmfMobilePhoneVerificationSave = useCallback(
    async ({ values: { validationCode } }: Pick<WizardContext, 'values'>) => {
      const code = validationCode as number;
      const response = await mobileVerificationCode({ code });
      await accountFetch();
      return handleEffectResponse(response);
    },
    [mobileVerificationCode, accountFetch]
  );

  const setJumpCreator = useCallback(async ({ data }: WizardContext) => {
    const rmfTargetToJump = data?.rmfTargetToJump;
    delete data.rmfTargetToJump;
    if (data?.cashierFlow === CashierFlowType.FirstWithdraw) {
      return {
        goJump: {
          target: rmfTargetToJump || '#withdraw.withdraw-flow-check',
          direction: JumpDirection.Next
        }
      };
    }
    if (data?.cashierFlow === CashierFlowType.FirstP2pTransfer) {
      return {
        goJump: {
          target: '#p-2-p-transfer.p-2-p-transfer-flow-check',
          direction: JumpDirection.Next
        }
      };
    }
    return {
      goJump: {
        target: rmfTargetToJump || '#deposit.deposit-process',
        direction: JumpDirection.Next
      }
    };
  }, []);

  const rmfAliasSave = useCallback(
    ({ values: { [aliasFieldName]: alias } }: Pick<WizardContext, 'values'>) => {
      if (isUndefined(alias) || isEmpty(alias)) return Promise.resolve({});

      return lastValueFrom(
        request({
          service: '/api/v1-preview/account',
          method: 'PATCH',
          body: { alias }
        }),
        { defaultValue: undefined }
      );
    },
    [request]
  );

  const rmfOccupationSave = useCallback(
    ({
      values: {
        profession,
        occupationDialingCode,
        occupationCompanyName,
        occupationCompanyPhone,
        occupationCompanyAddress
      }
    }: {
      values: {
        profession: string;
        occupationDialingCode: string;
        occupationCompanyName: string;
        occupationCompanyPhone: string;
        occupationCompanyAddress: string;
      };
    }) =>
      updateAccount({
        occupationInfo: {
          occupation: profession,
          occupationId: occupations.find(({ text }: StarsAutocompleteOption) => text === profession)
            ?.id,
          companyName: occupationCompanyName,
          companyPhone: formatPhoneNumber(occupationDialingCode, occupationCompanyPhone),
          companyAddress: occupationCompanyAddress
        }
      }),
    [updateAccount]
  );

  const rmfLossLimitSave = useCallback(
    ({
      values: {
        rmf: { loss }
      }
    }: {
      values: { rmf: { loss: ResponsibleGamingLimitsPayload & { skip?: boolean } } };
    }) => {
      if (loss.skip) return Promise.resolve({ ...loss });
      const limits = getLossLimitsPayload(loss);

      return Promise.all(
        limits.map(({ type, amount, currency }) =>
          lastValueFrom(
            request({
              service: 'responsiblegaming.updategamelimits',
              body: {
                amount,
                type,
                currency,
                passwordRequired: false
              }
            }),
            { defaultValue: undefined }
          )
        )
      );
    },
    [request]
  );

  const rmfGrPlayTimeLimitSave = useCallback(
    ({
      values: {
        rmf: { playTime }
      }
    }: {
      values: { rmf: { playTime: ResponsibleGamingLimitsPayload } };
    }) => {
      const limits = getPlayTimeLimitPayload(playTime);

      return Promise.all(
        limits.map(({ type, amount, currency }) =>
          lastValueFrom(
            request({
              service: 'responsiblegaming.updategamelimits',
              body: {
                amount,
                type,
                currency,
                passwordRequired: false
              }
            }),
            { defaultValue: undefined }
          )
        )
      );
    },
    [request]
  );

  const rmfTermsOfServiceSave = useCallback(
    async ({
      values: { playerIsNot3Party = false, pepHioSelection }
    }: {
      values: { pepHioSelection: PEPHIOOptions; playerIsNot3Party: boolean };
    }) => {
      const response = await updateAccount({
        playerIsNotPEP: pepHioSelection === PEPHIOOptions.NotPEPOrHIO,
        playerIsNot3Party,
        fetchAccount: false,
        timeout: 15000
      });
      if (response?.errors) {
        return handleEffectResponse(response);
      }
      return Promise.resolve({});
    },
    [updateAccount]
  );

  const rmfAccountFetch = ({ data }: WizardContext) => {
    // guard check is moved here because we hit wizard restriction of 3 nested Fork
    if (guards.doesJumpToFeatureStep({ data })) {
      return Promise.reject({
        body: {
          globalErrors: [],
          fieldErrors: [{ fieldName: PaySystemErrorCode.RMFRequired }]
        }
      });
    }
    try {
      return accountFetch();
    } catch {
      return Promise.resolve({});
    }
  };

  const rmfCitizenshipSave = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async ({ values: { citizenship, nationalDocumentNumber } }: any) => {
      const response = await updateAccount({
        citizenship,
        nationalDocumentNumber,
        fetchAccount: false
      });
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfGenderSave = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ values: { gender } }: any) =>
      lastValueFrom(
        request({
          service: '/api/v1-preview/account',
          method: 'PATCH',
          body: { gender }
        }),
        { defaultValue: undefined }
      ),
    [request]
  );

  const rmfPlaceOfBirthSave = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async ({ values: { countryOfBirth, stateOfBirth, cityOfBirth } }: any) => {
      const response = await updateAccount({
        placeOfBirthInfo: {
          countryOfBirth,
          stateOfBirth: stateOfBirth || countryOfBirth,
          cityOfBirth
        },
        fetchAccount: true
      });
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfAccumulatedLimitSave = useCallback(
    async ({
      values: {
        rmf: {
          weekly: { pokerLimit, sportLimit, pokerLimitChanged, sportLimitChanged }
        }
      }
    }: // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any) => {
      if (!pokerLimitChanged && !sportLimitChanged) return Promise.resolve({});

      const requests = [];
      pokerLimitChanged &&
        requests.push(
          lastValueFrom(
            request({
              service: 'responsiblegaming.updatebettinglimit',
              body: {
                extended: 0,
                weeklyLimit: pokerLimit,
                passwordRequired: false
              }
            }),
            { defaultValue: undefined }
          )
        );
      sportLimitChanged &&
        requests.push(
          lastValueFrom(
            request({
              service: 'responsiblegaming.updategamelimits',
              body: {
                amount: sportLimit,
                currency: Currency.Euro,
                type: LimitType.SPORT,
                passwordRequired: false
              }
            }),
            { defaultValue: undefined }
          )
        );

      return Promise.all(requests);
    },
    [request]
  );

  const rmfPokerTimeSave = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ values: { [pokerSliderFieldName]: timeLimit } }: any) =>
      lastValueFrom(
        request({
          service: 'responsiblegaming.updategamelimits',
          body: {
            amount: timeLimit * MINS_PER_HOUR,
            currency: Currency.Euro,
            type: LimitType.POKER_TIME,
            passwordRequired: false
          }
        }),
        { defaultValue: undefined }
      ),
    [request]
  );

  const rmfAutomaticWithdrawalLimitSave = useCallback(
    ({
      values: {
        rmf: { withdrawLimit }
      }
    }: // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any) =>
      lastValueFrom(
        request({
          service: 'responsiblegaming.updatebalancelimit',
          body: { newLimit: withdrawLimit, passwordRequired: false }
        }),
        { defaultValue: undefined }
      ),
    [request]
  );

  const rmfOccupationPEPSave = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async ({ values: { pepHioSelection } }: any) => {
      const response = await updatePEPHIOSelection({ pepHioSelection, rmf: true });
      return handleEffectResponse(response);
    },
    [updatePEPHIOSelection]
  );

  const rmfMobilePhoneSave = useCallback(
    async ({
      values: { dialingCode, mobilePhoneNumber }
    }: {
      values: {
        dialingCode: string;
        mobilePhoneNumber: number;
      };
    }) => {
      const mobilePhone = composePhoneNumber(dialingCode, mobilePhoneNumber);
      const response = await updateAccount({ mobilePhone });
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfDocumentsUpdate = useCallback(
    async ({
      values: {
        docExpiryDate,
        docIssueDate,
        docIssuePlace,
        docIssuingAuthority,
        docNumber,
        docType
      }
    }: Pick<WizardContext, 'values'>) => {
      const response = await updateAccount({
        docExpiryDate,
        docIssueDate,
        docIssuePlace,
        docIssuingAuthority,
        docNumber,
        docType
      });
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfEnableRealMoneyFlag = useCallback(async () => {
    const response = await updateAccount({ realMoneyFlag: true, fetchAccount: true });
    if (!response.success) {
      return handleEffectResponse(response);
    } else {
      if (canUseDOM) {
        dispatch.feature.check();
      }
      return { rmfPassed: true };
    }
  }, [updateAccount]);

  const rmfEnableRealMoneyFlagDE = useCallback(async () => {
    const response = await updateAccount({ realMoneyFlag: true, fetchAccount: true });

    if (response.regAction) {
      return deRmfHandleResponse(response);
    } else if (
      response.errors &&
      response.errors?.code == ErrorCode.BLACKLIST_SERVICE_NOT_AVAILABLE
    ) {
      return deRmfHandleResponse(response);
    } else if (!response.success) {
      return handleEffectResponse(response);
    } else {
      return { rmfPassed: true };
    }
  }, [updateAccount]);

  const rmfEnableRealMoneyFlagItaly = useCallback(
    async ({ values: { nationalDocumentNumber } }: Pick<WizardContext, 'values'>) => {
      const response = await updateAccount({ nationalDocumentNumber, realMoneyFlag: true });

      if (!response.success) {
        return handleEffectResponse(response);
      } else {
        return { rmfPassed: true };
      }
    },
    [updateAccount]
  );

  const rmfSecurityQuestionsSave = useCallback(
    async ({
      values: { question1, question2, answer1, answer2 }
    }: Pick<WizardContext, 'values'>) => {
      const response = await updateAccount({ question1, question2, answer1, answer2 });
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfSSNConfirm = useCallback(
    async ({ values: { nationalDocumentNumber } }: Pick<WizardContext, 'values'>) => {
      const response = await updateAccount({
        nationalDocumentNumber,
        realMoneyFlag: true,
        fetchAccount: true
      });

      if (!response.success) {
        const errorCode = response?.errors?.code?.toString();

        if (errorCode === VerificationErrorCode.FiscalCodeLen9Required) {
          return { SSNConfirmationRequired: true };
        }

        if (errorCode === VerificationErrorCode.FiscalCodeAlreadyExists) {
          return handleEffectResponse(response);
        }
      } else {
        return handleEffectResponse(response);
      }
    },
    [updateAccount]
  );

  const rmfUSPASSNConfirm = useCallback(
    async ({ values: { nationalDocumentNumber } }: Pick<WizardContext, 'values'>) => {
      const response = await updateAccount({
        nationalDocumentNumber,
        realMoneyFlag: true,
        fetchAccount: true
      });

      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const rmfSSNSave = useCallback(
    async ({
      values: { nationalDocumentNumber, ssn1, ssn2, ssn3 }
    }: Pick<WizardContext, 'values' | 'data'>) => {
      if (ssn1 && ssn2 && ssn3 && !nationalDocumentNumber) {
        nationalDocumentNumber = ssn1 + ssn2 + ssn3;
      }

      const response = await updateAccount({
        nationalDocumentNumber,
        realMoneyFlag: true,
        fetchAccount: true
      });

      if (!response.success) {
        return handleEffectResponse(response);
      } else {
        return {
          kbaQuestions: response.kbaQuestions,
          kbaQuestionsPointer: { current: 0, prev: -1, next: 1 },
          kbaAnswers: [],
          rmfPassed: true
        };
      }
    },
    [updateAccount]
  );

  const rmfEnableRealMoneyFlagSpain = useCallback(
    async ({
      data: { userInfoFields },
      values: { secondLastName, dialingCode, mobilePhoneNumber }
    }: {
      data: { userInfoFields: Fields | undefined };
      values: { secondLastName: string; dialingCode: string; mobilePhoneNumber: string };
    }) => {
      const mobilePhone = composePhoneNumber(dialingCode, mobilePhoneNumber);
      const secondLastNameField = userInfoFields?.find(({ name }) => name === 'secondLastName');

      const payload = secondLastNameField?.value
        ? { realMoneyFlag: true, mobilePhone }
        : { realMoneyFlag: true, secondLastName, mobilePhone };

      const response = await updateAccount(payload);

      if (!response.success) {
        return handleEffectResponse(response);
      } else {
        return { rmfPassed: true };
      }
    },
    [updateAccount]
  );

  const rmfGetBankDetailsInfo = useCallback(async () => {
    const { body: dbtFieldsData } = await lastValueFrom(
      request({ service: 'wc3.cards.get.dbtFields' })
    );

    return { dbtFieldsData };
  }, [request]);

  const rmfUpdateBankDetailsInfo = useCallback(
    async ({
      data: { dbtFieldsData },
      values: { firstName, lastName, BANKNAME, BANKCOUNTRY, IBAN, BIC }
    }: WizardContext) => {
      const [currency] = dbtFieldsData.currencies;
      return await lastValueFrom(
        request({
          service: 'wc3.cards.update.dbtFields',
          body: {
            currency,
            fields: { ACCOUNTNAME: `${firstName} ${lastName}`, BANKNAME, BANKCOUNTRY, IBAN, BIC }
          }
        })
      );
    },
    [request]
  );

  const rmfEnableRealMoneyFlagDK = useCallback(
    async ({ data: { mitIdResultContext } }: Pick<WizardContext, 'data'>) => {
      const payload = mitIdResultContext
        ? { realMoneyFlag: true, ...mitIdResultContext }
        : { realMoneyFlag: true };

      const response = await updateAccount(payload);
      if (!response.success) {
        return handleEffectResponse(response);
      } else {
        return { rmfPassed: true };
      }
    },
    [updateAccount]
  );

  const rmfMitIdData = () => setMitIdData;

  return {
    [Service.RmfAccountFetch]: rmfAccountFetch,
    [Service.RmfAccumulatedLimitSave]: rmfAccumulatedLimitSave,
    [Service.RmfAddressDESave]: rmfAddressDESave,
    [Service.RmfAddressSave]: rmfAddressSave,
    [Service.RmfAliasSave]: rmfAliasSave,
    [Service.RmfAutomaticWithdrawalLimitSave]: rmfAutomaticWithdrawalLimitSave,
    [Service.RmfBirthNameSave]: rmfBirthNameSave,
    [Service.RmfCitizenshipSave]: rmfCitizenshipSave,
    [Service.RmfDepositLimitSave]: rmfDepositLimitSave,
    [Service.RmfFetchApprovedLossLimits]: rmfFetchApprovedLossLimits,
    [Service.RmfFetchUserDepositLimit]: rmfFetchUserDepositLimit,
    [Service.RmfDobUpdate]: rmfDobUpdate,
    [Service.RmfDocumentsUpdate]: rmfDocumentsUpdate,
    [Service.RmfEnableRealMoneyFlag]: rmfEnableRealMoneyFlag,
    [Service.RmfGenderSave]: rmfGenderSave,
    [Service.RmfGetBankDetailsInfo]: rmfGetBankDetailsInfo,
    [Service.RmfGetFiscalCodeInfo]: rmfGetFiscalCodeInfo,
    [Service.RmfIdentifyVerificationSave]: rmfIdentifyVerificationSave,
    [Service.RmfIdentifyVerificationSavePT]: rmfIdentifyVerificationSavePT,
    [Service.RmfLegalNameSave]: rmfLegalNameSave,
    [Service.RmfLossLimitSave]: rmfLossLimitSave,
    [Service.RmfGRPlayingTimeSave]: rmfGrPlayTimeLimitSave,
    [Service.RmfMiddleNameSave]: rmfMiddleNameSave,
    [Service.RmfOccupationPEPSave]: rmfOccupationPEPSave,
    [Service.RmfOccupationSave]: rmfOccupationSave,
    [Service.RmfPlaceOfBirthSave]: rmfPlaceOfBirthSave,
    [Service.RmfPokerTimeSave]: rmfPokerTimeSave,
    [Service.RmfMobilePhoneSave]: rmfMobilePhoneSave,
    [Service.RmfMobilePhoneVerificationSave]: rmfMobilePhoneVerificationSave,
    [Service.RmfRealMoneyFlagAndAddressUpdate]: rmfRealMoneyFlagAndAddressUpdate,
    [Service.RmfRealMoneyInformationCheck]: rmfRealMoneyInformationCheck,
    [Service.RmfUpdateFormData]: rmfUpdateFormData,
    [Service.RmfUpdateUSPAUserData]: rmfUpdateUSPAUserData,
    [Service.RmfUpdateCanadaFormFields]: rmfUpdateCanadaFormFields,
    [Service.RmfSecurityQuestionsSave]: rmfSecurityQuestionsSave,
    [Service.RmfSSNSave]: rmfSSNSave,
    [Service.RmfSSNConfirm]: rmfSSNConfirm,
    [Service.RmfUSPASSNConfirm]: rmfUSPASSNConfirm,
    [Service.RmfTermsOfServiceSave]: rmfTermsOfServiceSave,
    [Service.RmfUpdateBankDetailsInfo]: rmfUpdateBankDetailsInfo,
    [Service.SetCRMBonus]: setCRMBonus,
    [Service.SetJumpCreator]: setJumpCreator,
    [Service.SignupDobFetch]: signupDobFetch,
    [Service.RmfUserDataSave]: rmfUserDataSave,
    [Service.RmfUSPAUserDataSave]: rmfUSPAUserDataSave,
    [Service.RmfRetryUserDataSave]: rmfUserDataSave,
    [Service.RmfRetryCAONUserDataSave]: rmfRetryCAONUserDataSave,
    [Service.RmfRetryFRUserDataSave]: rmfRetryFRUserDataSave,
    [Service.RmfRetryDEUserDataSave]: rmfRetryDEUserDataSave,
    [Service.RmfRetryInvokeRealMoneyOk]: rmfEnableRealMoneyFlag,
    [Service.RmfTermsOfServiceWithoutLimitSave]: rmfTermsOfServiceSave,
    [Service.RmfCrossOperatorDepositLimitSave]: rmfCrossOperatorDepositLimitSave,
    [Service.RmfEnableRealMoneyFlagDE]: rmfEnableRealMoneyFlagDE,
    [Service.RmfEnableRealMoneyFlagItaly]: rmfEnableRealMoneyFlagItaly,
    [Service.RmfEnableRealMoneyFlagSpain]: rmfEnableRealMoneyFlagSpain,
    [Service.RmfIdentifyVerificationSaveDK]: rmfIdentifyVerificationSaveDK,
    [Service.RmfIdentifyVerificationSaveGR]: rmfIdentifyVerificationSaveGR,
    [Service.RmfMitIdData]: rmfMitIdData,
    [Service.RmfEnableRealMoneyFlagDK]: rmfEnableRealMoneyFlagDK
  };
};

export default useRmfServicesData;
